import React, { createContext, useContext, ReactNode } from 'react'
import { CheckoutFlow } from '../flow/type'

/**
 * Flow context value containing the current checkout flow name
 */
export interface FlowContextValue {
  flowName: CheckoutFlow
}

/**
 * React Context for sharing the current checkout flow across the component tree
 *
 * The flow name comes from server-side props (via withApplePaylaterProps or similar)
 * and is used by useAppRouter to automatically prefix routes based on the active flow.
 *
 * @example
 * ```tsx
 * // In _app.tsx
 * const flowName = pageProps.flowName || null
 * return (
 *   <FlowProvider flowName={flowName}>
 *     <Component {...pageProps} />
 *   </FlowProvider>
 * )
 * ```
 *
 * @example
 * ```tsx
 * // In a component
 * const { flowName } = useFlowContext()
 * if (flowName === 'apple-paylater') {
 *   // Apple Pay Later specific logic
 * }
 * ```
 */
const FlowContext = createContext<FlowContextValue | undefined>(undefined)

/**
 * Props for the FlowProvider component
 */
export interface FlowProviderProps {
  /**
   * The current checkout flow name
   * - 'default': Regular checkout flow (no prefix)
   * - 'apple-paylater': Apple Pay Later flow (routes prefixed with /apple-paylater)
   */
  flowName: CheckoutFlow
  /**
   * Child components
   */
  children: ReactNode
}

/**
 * Provider component that makes the current flow available to all child components
 *
 * Should be placed in _app.tsx to wrap the entire application, extracting flowName
 * from pageProps passed by server-side rendering.
 *
 * @param props - The provider props
 * @returns The provider component
 */
export const FlowProvider: React.FC<FlowProviderProps> = ({ flowName, children }) => {
  const value: FlowContextValue = { flowName }

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
}

/**
 * Hook to access the current checkout flow from any component
 *
 * This hook must be used within a FlowProvider. It provides access to the current
 * flow name which is used by useAppRouter for automatic route prefixing.
 *
 * @throws {Error} If used outside of a FlowProvider
 * @returns The flow context value containing the current flow name
 *
 * @example
 * ```tsx
 * const { flowName } = useFlowContext()
 *
 * // Use flow name for conditional logic
 * if (flowName === 'apple-paylater') {
 *   return <ApplePaylaterSpecificComponent />
 * }
 *
 * // Or use it in useEffect
 * useEffect(() => {
 *   if (flowName) {
 *     trackFlowView(flowName)
 *   }
 * }, [flowName])
 * ```
 */
export const useFlowContext = (): FlowContextValue => {
  const context = useContext(FlowContext)

  if (context === undefined) {
    throw new Error('useFlowContext must be used within a FlowProvider. Make sure you have wrapped your app with <FlowProvider> in _app.tsx.')
  }

  return context
}
